import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { ChangeEventHandler, JSX } from "react";

type Props = {
  className?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder: string;
  value?: any;
};

/**
 * A text input component with a search icon, designed for global filtering.
 *
 * @param {Props} props - The properties for the global filter component.
 * @returns {JSX.Element} The rendered global filter input component.
 *
 * @example
 * ```tsx
 * import { useState } from "react";
 * import TwGlobalFilter from "./TwGlobalFilter";
 *
 * function MyComponent() {
 *   const [filterValue, setFilterValue] = useState<string>("");
 *
 *   const handleFilterChange: ChangeEventHandler<HTMLInputElement> = (event) => {
 *     setFilterValue(event.target.value);
 *   };
 *
 *   return (
 *     <TwGlobalFilter
 *       placeholder="Search..."
 *       value={filterValue}
 *       onChange={handleFilterChange}
 *     />
 *   );
 * }
 * ```
 */
function TwGlobalFilter(props: Props): JSX.Element {
  // Defaulting value to an empty string if it's undefined or null
  const value = props.value !== undefined && props.value !== null ? props.value : "";

  return (
    <Card data-cy="twglobalfilter" className={`${props.className} flex flex-row bg-transparent dark:bg-transparent items-center p-2 px-3 h-[36px] shadow-none`}>
      <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
      <Input
        data-cy="twglobalfilter-input"
        autoComplete="off"
        className="placeholder:text-xs placeholder:md:text-sm w-full focus:outline-hidden bg-transparent dark:bg-transparent dark:text-white
                  outline-none border-none focus-visible:ring-0 focus-visible:ring-offset-0"
        type="text"
        id="textFilterInput"
        value={value}
        placeholder={props.placeholder}
        onChange={props.onChange}
      />
    </Card>
  );
}

export default TwGlobalFilter;
